import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAdminApplications } from '@/features/admin/hooks/useAdminApplications'
import { adminProductionsService } from '../services/adminProductions.service'
import type { ProductionDocument, ProductionStatus, ProductionType } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  productionId: string
  production: ProductionDocument
  onSaved: () => void
}

const TYPE_OPTIONS: Array<{ value: ProductionType; label: string }> = [
  { value: 'festival', label: 'Festival' },
  { value: 'showcase', label: 'Showcase' },
  { value: 'film_screening', label: 'Film Screening' },
  { value: 'general', label: 'General' },
]

const STATUS_OPTIONS: Array<{ value: ProductionStatus; label: string }> = [
  { value: 'planning', label: 'Planning' },
  { value: 'intake_open', label: 'Intake Open' },
  { value: 'curating', label: 'Curating' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
]

export function EditProductionDialog({
  open,
  onOpenChange,
  productionId,
  production,
  onSaved,
}: Props) {
  const { applications } = useAdminApplications()
  const [name, setName] = useState(production.name)
  const [slug, setSlug] = useState(production.slug)
  const [type, setType] = useState<ProductionType>(production.type as ProductionType)
  const [status, setStatus] = useState<ProductionStatus>(production.status)
  const [venue, setVenue] = useState(production.venue)
  const [startDate, setStartDate] = useState(production.startDate)
  const [endDate, setEndDate] = useState(production.endDate ?? '')
  const [driveLink, setDriveLink] = useState(production.driveLink ?? '')
  const [intakeApplicationId, setIntakeApplicationId] = useState(
    production.intakeApplicationId ?? ''
  )
  const [techApplicationId, setTechApplicationId] = useState(
    production.techApplicationId ?? ''
  )
  const [isArchived, setIsArchived] = useState(production.isArchived)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset on open so reopening after a save shows fresh-from-Firestore values
  useEffect(() => {
    if (!open) return
    setName(production.name)
    setSlug(production.slug)
    setType(production.type as ProductionType)
    setStatus(production.status)
    setVenue(production.venue)
    setStartDate(production.startDate)
    setEndDate(production.endDate ?? '')
    setDriveLink(production.driveLink ?? '')
    setIntakeApplicationId(production.intakeApplicationId ?? '')
    setTechApplicationId(production.techApplicationId ?? '')
    setIsArchived(production.isArchived)
    setError(null)
  }, [open, production])

  const intakeApplications = useMemo(
    () => applications.filter((a) => a.applicationType === 'production_intake'),
    [applications]
  )
  const techApplications = useMemo(
    () => applications.filter((a) => a.applicationType === 'tech_questionnaire'),
    [applications]
  )

  async function save() {
    setError(null)
    if (!name.trim()) return setError(withErrorCode('Name is required.', 'app/validation'))
    if (!slug.trim()) return setError(withErrorCode('Slug is required.', 'app/validation'))
    if (!/^[a-z0-9-]+$/.test(slug)) {
      return setError(withErrorCode('Slug may only contain lowercase letters, numbers, and hyphens.', 'app/validation'))
    }
    if (!venue.trim()) return setError(withErrorCode('Venue is required.', 'app/validation'))
    if (!startDate) return setError(withErrorCode('Start date is required.', 'app/validation'))
    if (endDate && endDate < startDate) {
      return setError(withErrorCode('End date cannot be earlier than start date.', 'app/validation'))
    }

    setSubmitting(true)
    try {
      await adminProductionsService.update(productionId, {
        name: name.trim(),
        slug: slug.trim(),
        type,
        status,
        venue: venue.trim(),
        startDate,
        endDate,
        driveLink,
        intakeApplicationId,
        techApplicationId,
      })
      if (isArchived !== production.isArchived) {
        await adminProductionsService.setArchived(productionId, isArchived)
      }
      onSaved()
      onOpenChange(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save production.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Production</DialogTitle>
          <DialogDescription>
            All fields are editable. Slug uniqueness is enforced.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="edit-name">Name</Label>
            <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit-slug">Slug</Label>
            <Input
              id="edit-slug"
              className="font-mono"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as ProductionType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as ProductionStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit-venue">Venue</Label>
            <Input id="edit-venue" value={venue} onChange={(e) => setVenue(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="edit-start">Start Date</Label>
              <Input
                id="edit-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-end">End Date</Label>
              <Input
                id="edit-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit-drive">Google Drive Link</Label>
            <Input
              id="edit-drive"
              value={driveLink}
              onChange={(e) => setDriveLink(e.target.value)}
              placeholder="https://drive.google.com/..."
            />
          </div>
          <div className="space-y-1">
            <Label>Intake Application</Label>
            <Select
              value={intakeApplicationId || '__none__'}
              onValueChange={(v) => setIntakeApplicationId(v === '__none__' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {intakeApplications.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Tech Questionnaire</Label>
            <Select
              value={techApplicationId || '__none__'}
              onValueChange={(v) => setTechApplicationId(v === '__none__' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {techApplications.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Optional. When set, artists who accept their spot fill this out inline on the
              confirmation page before their acceptance is complete.
            </p>
          </div>
          <label className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={isArchived}
              onChange={(e) => setIsArchived(e.target.checked)}
            />
            <span className="text-sm">Archived (hidden from default list)</span>
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={save} disabled={submitting}>
            {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
